import { TextInput, CustomButton, CustomSelect, RadioGroup } from '@/shared/ui/components';
import { INSURANCE_TYPE_OPTIONS, PRE_AUTHORIZATION_OPTIONS } from '@/shared/config';
import { t } from 'ttag';
import { useInsuranceDrawer } from './useInsuranceDrawer';
import type { InsuranceDrawerProps } from './InsuranceDrawer.types';
import './style.scss';

/**
 * Renders the patient insurance drawer with an insurance type select, an optional provider text field and a pre-authorization radio group bound to the useInsuranceDrawer state. Cancel is disabled and save shows a spinner while a save request is in flight.
 *
 * @param data - Existing insurance values used to seed the form.
 * @param handleClick - Callback closing the drawer, also wired to the cancel button.
 * @param isSaving - Whether a save request is in flight, disabling cancel and loading save.
 * @returns The insurance editing form with action buttons.
 */
const InsuranceDrawer = ({ data, handleClick, isSaving }: InsuranceDrawerProps) => {
  const { state, handleChange, onClickBottomButton } = useInsuranceDrawer({ data, handleClick });

  return (
    <div className="list-input">
      <CustomSelect
        title={t`Insurance type`}
        aria-label={t`Insurance type`}
        showSearch
        allowClear={false}
        name="type"
        size="middle"
        options={INSURANCE_TYPE_OPTIONS}
        value={state.type}
        onChange={handleChange}
      />

      <TextInput
        name="provider"
        label={t`Provider (optional)`}
        placeholder={t`Enter provider`}
        value={state.provider}
        onChange={handleChange}
      />

      <div>
        <span className="custom-text-input__label">{t`Pre-authorization required`}</span>
        <div className="mt1">
          <RadioGroup
            className="radio-input"
            value={state.preAuth}
            name="preAuth"
            onChange={handleChange}
            options={PRE_AUTHORIZATION_OPTIONS}
            size="small"
          />
        </div>
      </div>

      <div className="bottom-button-row">
        <CustomButton
          id="cancel-btn"
          style="neutral"
          type="outline"
          onClick={handleClick}
          styles={{ minWidth: 80 }}
          disabled={isSaving}
        >
          {t`Cancel`}
        </CustomButton>
        <CustomButton
          id="save-btn"
          onClick={onClickBottomButton}
          styles={{ minWidth: 80 }}
          loading={isSaving}
        >
          {t`Save`}
        </CustomButton>
      </div>
    </div>
  );
};

export default InsuranceDrawer;
